import { css } from '@emotion/react'
import { Portal, Theme, useTheme } from 'bold-ui'
import {
  cloneElement,
  HTMLAttributes,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  TouchEvent,
  useEffect,
  useState,
} from 'react'
import { usePopper } from 'react-popper'

type MarkerTriggerProps = Pick<
  HTMLAttributes<HTMLElement>,
  'aria-describedby' | 'onBlur' | 'onClick' | 'onFocus' | 'onKeyDown' | 'onMouseEnter' | 'onMouseLeave' | 'onTouchStart'
>

interface MapaTerritorioMarkerHoverProps {
  children: ReactElement<MarkerTriggerProps>
  hoverContent: ReactNode
  tooltipId: string
}

const HOVER_CARD_DELAY_MS = 300

export function MapaTerritorioMarkerHover(props: Readonly<MapaTerritorioMarkerHoverProps>) {
  const { children, hoverContent, tooltipId } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const [isHoverCardVisible, setIsHoverCardVisible] = useState(false)
  const [isMouseOver, setIsMouseOver] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [markerRef, setMarkerRef] = useState<HTMLDivElement | null>(null)
  const [hoverCardRef, setHoverCardRef] = useState<HTMLDivElement | null>(null)

  const isTriggerActive = isMouseOver || isFocused

  useEffect(() => {
    if (!isTriggerActive) {
      setIsHoverCardVisible(false)
      return
    }

    const timeout = window.setTimeout(() => setIsHoverCardVisible(true), HOVER_CARD_DELAY_MS)
    return () => window.clearTimeout(timeout)
  }, [isTriggerActive])

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    event.currentTarget.focus()
    setIsHoverCardVisible(true)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setIsHoverCardVisible(false)
    }
  }

  const originalAriaDescribedBy = children.props['aria-describedby']
  const ariaDescribedBy = isHoverCardVisible
    ? [originalAriaDescribedBy, tooltipId].filter(Boolean).join(' ')
    : originalAriaDescribedBy

  const trigger = cloneElement(children, {
    'aria-describedby': ariaDescribedBy,
    onClick: (event) => {
      setIsHoverCardVisible(false)
      setIsMouseOver(false)
      setIsFocused(false)
      children.props.onClick?.(event)
    },
    onMouseEnter: () => setIsMouseOver(true),
    onMouseLeave: () => setIsMouseOver(false),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onTouchStart: handleTouchStart,
    onKeyDown: handleKeyDown,
  })

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(markerRef, hoverCardRef, {
    placement: 'right',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  })

  return (
    <>
      <div ref={setMarkerRef} css={styles.marker} data-testid='mapa-territorio-marker'>
        {trigger}
      </div>

      {isHoverCardVisible && (
        <Portal>
          <div
            ref={setHoverCardRef}
            css={styles.popper}
            style={popperStyles.popper}
            id={tooltipId}
            role='tooltip'
            {...popperAttributes.popper}
          >
            {hoverContent}
          </div>
        </Portal>
      )}
    </>
  )
}

const createStyles = (theme: Theme) => ({
  marker: css`
    display: block;
    width: 2rem;
    height: 2.5rem;
    border-radius: ${theme.radius.button}px;
    transition: transform 0.1s ease-out;
    transform-origin: bottom center;

    &:hover {
      transform: scale(1.0333);
    }
  `,
  popper: css`
    z-index: ${theme.zIndex.tooltip};
    pointer-events: none;
  `,
})
